// frontend/src/pages/ScriptGenerator.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguageStore } from '../store/useLanguageStore';
import { useUIStore } from '../store/useUIStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { apiClient } from '../api/client';
import { AppSidebar } from '../components/AppSidebar';
import { Modal } from '../components/Modal';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileCode2, Loader2, Menu, LogOut, X, Plus, Trash2,
  Play, AlertTriangle, Download, Copy, Check, Terminal, Cpu,
  Clock, ChevronLeft, ChevronRight, Eye, RefreshCw, Zap, Sparkles,
  Package, FileText, TestTube2, BookOpen, Star, CopyPlus,
} from 'lucide-react';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';

// ── Constants ────────────────────────────────────────────────────────
const LANGUAGES = [
  { value: 'python', label: 'Python', icon: '🐍' },
  { value: 'nodejs', label: 'Node.js', icon: '🟢' },
  { value: 'csharp', label: 'C# (.NET)', icon: '🔷' },
];

const PARITY_OPTIONS = [
  { value: 'N', label: 'None' },
  { value: 'E', label: 'Even' },
  { value: 'O', label: 'Odd' },
];

const BAUDRATE_OPTIONS = [9600, 19200, 38400, 57600, 115200];

const DB_COLUMN_TYPES = [
  'VARCHAR(255)', 'VARCHAR(100)', 'VARCHAR(50)', 'TEXT',
  'INT', 'BIGINT', 'DECIMAL(10,2)', 'DECIMAL(5,2)', 'DECIMAL(15,4)', 'FLOAT',
  'DATE', 'DATETIME', 'TIMESTAMP', 'BOOLEAN', 'TINYINT',
];
const VALID_SQL_BASES = new Set([
  'INT', 'INTEGER', 'TINYINT', 'SMALLINT', 'MEDIUMINT', 'BIGINT',
  'FLOAT', 'DOUBLE', 'REAL', 'DECIMAL', 'NUMERIC', 'NUMBER',
  'CHAR', 'VARCHAR', 'VARCHAR2', 'NCHAR', 'NVARCHAR', 'NVARCHAR2',
  'TEXT', 'TINYTEXT', 'MEDIUMTEXT', 'LONGTEXT', 'NTEXT', 'CLOB', 'NCLOB',
  'BLOB', 'TINYBLOB', 'MEDIUMBLOB', 'LONGBLOB', 'BINARY', 'VARBINARY',
  'RAW', 'LONG', 'BYTEA', 'IMAGE',
  'DATE', 'TIME', 'DATETIME', 'DATETIME2', 'TIMESTAMP', 'YEAR',
  'BOOLEAN', 'BOOL', 'BIT',
  'JSON', 'JSONB', 'XML',
  'UUID', 'SERIAL', 'BIGSERIAL', 'SMALLSERIAL',
  'MONEY', 'SMALLMONEY', 'INET', 'CIDR', 'MACADDR',
  'ENUM', 'SET', 'ARRAY',
  'UNIQUEIDENTIFIER', 'SQL_VARIANT', 'ROWID',
]);

const isValidDbType = (input) => {
  const v = (input || '').trim().toUpperCase();
  if (!v) return false;
  const match = v.match(/^([A-Z_][A-Z0-9_]*)/);
  if (!match) return false;
  return VALID_SQL_BASES.has(match[1]);
};
const ITEMS_PER_PAGE = 8;
const LICENSE_PRESETS = [
  { value: 30, label: '30 días' },
  { value: 90, label: '90 días' },
  { value: 180, label: '180 días' },
  { value: 365, label: '1 año' },
  { value: 0, label: 'Sin expiración' },
];
const PROTOCOLS = [
  { value: 'serial', label: 'RS232C / Serial', desc: 'Balanzas, CNC, medidores' },
  { value: 'modbus', label: 'Modbus RTU/TCP', desc: 'PLCs, sensores, medidores de energía' },
  { value: 'tcp_socket', label: 'TCP/IP Socket', desc: 'Máquinas con Ethernet, impresoras' },
  { value: 'mqtt', label: 'MQTT', desc: 'IoT, sensores modernos, gateways' },
];

export const ScriptGenerator = ({ user, setUser }) => {
  const { t, language: uiLang } = useLanguageStore();
  const { openSidebar } = useUIStore();
  const navigate = useNavigate();

  // ── Historial state ────────────────────────────────────────────────
  const [scripts, setScripts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Active tab ─────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('pipeline'); // pipeline | industrial
  const [useAI, setUseAI] = useState(false);              // Toggle IA en tab pipeline
  const [protocol, setProtocol] = useState('serial');      // Protocolo industrial

  // ── Modbus fields ──────────────────────────────────────────────────
  const [modbusType, setModbusType] = useState('TCP');
  const [modbusHost, setModbusHost] = useState('192.168.1.100');
  const [modbusPort, setModbusPort] = useState(502);
  const [slaveId, setSlaveId] = useState(1);
  const [registerAddress, setRegisterAddress] = useState(0);
  const [registerCount, setRegisterCount] = useState(10);
  const [pollingInterval, setPollingInterval] = useState(5);

  // ── TCP Socket fields ──────────────────────────────────────────────
  const [tcpHost, setTcpHost] = useState('192.168.1.100');
  const [tcpPort, setTcpPort] = useState(9100);
  const [tcpDelimiter, setTcpDelimiter] = useState('newline');
  const [tcpTimeout, setTcpTimeout] = useState(10);

  // ── MQTT fields ────────────────────────────────────────────────────
  const [mqttHost, setMqttHost] = useState('localhost');
  const [mqttPort, setMqttPort] = useState(1883);
  const [mqttTopics, setMqttTopics] = useState('planta/sensor1');
  const [mqttQos, setMqttQos] = useState(0);
  const [mqttUser, setMqttUser] = useState('');
  const [mqttPayloadFormat, setMqttPayloadFormat] = useState('json');

  // ── Pipeline tab ───────────────────────────────────────────────────
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [language, setLanguage] = useState('python');
  const [includeLogging, setIncludeLogging] = useState(true);
  const [includeRetry, setIncludeRetry] = useState(true);
  const [includeSchedule, setIncludeSchedule] = useState(false);
  const [customHeader, setCustomHeader] = useState('');
  const [scriptName, setScriptName] = useState('');

  // ── Serial tab ─────────────────────────────────────────────────────
  const [serialPort, setSerialPort] = useState('/dev/ttyUSB0');
  const [baudrate, setBaudrate] = useState(9600);
  const [parity, setParity] = useState('N');
  const [stopbits, setStopbits] = useState(1);
  const [parseRegex, setParseRegex] = useState('ST,GS,\\s*([\\d.]+),(\\w+)');
  const [serialMode, setSerialMode] = useState('daemon');
  const [cronInterval, setCronInterval] = useState(5);
  const [testString, setTestString] = useState('ST,GS, 15.50,kg');
  const [regexPreview, setRegexPreview] = useState([]);
  const [destEngine, setDestEngine] = useState('mysql');
  const [destTable, setDestTable] = useState('mediciones');
  const [destColumns, setDestColumns] = useState([
    { name: 'valor', type: 'DECIMAL(10,2)' },
    { name: 'unidad', type: 'VARCHAR(10)' },
  ]);
  // const [editingTypeIdx, setEditingTypeIdx] = useState(null);
  // const [customTypeInput, setCustomTypeInput] = useState('');
  const [editingTypeIdx, setEditingTypeIdx] = useState(null);

  // ── Generation state ───────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedScript, setGeneratedScript] = useState('');
  const [generatedFilename, setGeneratedFilename] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [clientName, setClientName] = useState('');
  const [licenseDays, setLicenseDays] = useState(90);
  const [licenseClient, setLicenseClient] = useState('');

  // ── Generated package extras ─────────────────────────────────────
  const [generatedTest, setGeneratedTest] = useState('');
  const [generatedGuide, setGeneratedGuide] = useState('');
  const [generatedTestFilename, setGeneratedTestFilename] = useState('');
  const [previewTab, setPreviewTab] = useState('script'); // script | test | guide

  // ── View existing script ───────────────────────────────────────────
  const [viewingScript, setViewingScript] = useState(null);
  const [loadingView, setLoadingView] = useState(false);

  // ── History filters ─────────────────────────────────────────────────
  const [filterLang, setFilterLang] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // ── Cost estimate ───────────────────────────────────────────────────
  const [estimatedCost, setEstimatedCost] = useState(null);

  // ── Confirm modal ──────────────────────────────────────────────────
  const [modalConfig, setModalConfig] = useState({
    isOpen: false, title: '', message: '', showCancel: false, onAccept: null,
  });
  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  // ── Logout ─────────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
    navigate('/login');
  };

  // ── Fetch pipelines + scripts + client name ────────────────────────
  const fetchData = useCallback(async () => {
    setLoadingList(true);
    try {
      const [pipeRes, scriptRes] = await Promise.all([
        apiClient.get('/dashboard/pipelines'),
        apiClient.get('/dashboard/scripts'),
      ]);
      if (pipeRes.data.success) {
        setPipelines(pipeRes.data.data || []);
        if (pipeRes.data.clientName) setClientName(pipeRes.data.clientName);
      }
      if (scriptRes.data.success) {
        setScripts(scriptRes.data.data || []);
      }
    } catch (err) {
      console.error('Error cargando datos:', err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Reset page on filter change ─────────────────────────────────────
  useEffect(() => { setCurrentPage(1); }, [filterLang, filterType]);

  // ── Cost estimation (when AI toggle is ON in pipeline tab) ──────────
  useEffect(() => {
    if (!useAI || activeTab !== 'pipeline' || !selectedPipeline) {
      setEstimatedCost(null);
      return;
    }
    const pipe = pipelines.find(p => String(p.id) === String(selectedPipeline));
    if (!pipe) { setEstimatedCost(null); return; }
    const fieldCount = pipe.fieldCount || 10;
    const transformCount = pipe.transformCount || 0;
    apiClient.post('/dashboard/scripts/estimate-cost', { fieldCount, transformCount })
      .then(res => { if (res.data.success) setEstimatedCost(res.data); })
      .catch(() => setEstimatedCost(null));
  }, [useAI, selectedPipeline, activeTab, pipelines]);

  // ── Regex preview en vivo ──────────────────────────────────────────
  const [regexError, setRegexError] = useState('');
  useEffect(() => {
    if (!parseRegex) { setRegexPreview([]); setRegexError(''); return; }
    try {
      const re = new RegExp(parseRegex);
      // Contar grupos de captura: paréntesis abiertos que NO sean (?
      const groupCount = (parseRegex.match(/\((?!\?)/g) || []).length;
      if (groupCount === 0) {
        setRegexError('La regex debe tener al menos un grupo de captura (...)');
        setRegexPreview([]);
        return;
      }
      setRegexError('');
      if (!testString) { setRegexPreview([]); return; }
      const match = testString.match(re);
      setRegexPreview(match ? match.slice(1) : []);
    } catch (err) {
      setRegexError(err.message);
      setRegexPreview([]);
    }
  }, [testString, parseRegex]);

  // ── Limpiar preview al cambiar configuración ───────────────────────
  useEffect(() => {
    setGeneratedScript('');
    setViewingScript(null);
    setGeneratedTest('');
    setGeneratedGuide('');
    setGeneratedTestFilename('');
  }, [selectedPipeline, language, activeTab, useAI, protocol, scriptName]);


  // ── Columns management ─────────────────────────────────────────────
  const addColumn = () => setDestColumns(prev => [...prev, { name: '', type: 'VARCHAR(255)' }]);
  const removeColumn = (idx) => setDestColumns(prev => prev.filter((_, i) => i !== idx));
  const updateColumn = (idx, field, value) => {
    setDestColumns(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  // ── Generate handler ───────────────────────────────────────────────
  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');
    setGeneratedScript('');

    try {
      let payload;
      if (activeTab === 'industrial') {
        // Construir config según protocolo
        const baseIndustrial = {
          mode: serialMode,
          cronInterval: serialMode === 'scheduled' ? cronInterval : undefined,
          customHeader,
          destination: {
            engine: destEngine, table: destTable,
            columns: destColumns.filter(c => c.name.trim()),
          },
        };
        if (protocol === 'serial') {
          Object.assign(baseIndustrial, { port: serialPort, baudrate, parity, stopbits, parseRegex });
        } else if (protocol === 'modbus') {
          Object.assign(baseIndustrial, { modbusType, host: modbusHost, port: modbusPort, slaveId, registerAddress, registerCount, pollingInterval, parseRegex });
        } else if (protocol === 'tcp_socket') {
          Object.assign(baseIndustrial, { host: tcpHost, port: tcpPort, delimiter: tcpDelimiter, timeout: tcpTimeout, parseRegex });
        } else if (protocol === 'mqtt') {
          Object.assign(baseIndustrial, { host: mqttHost, port: mqttPort, topics: mqttTopics, qos: mqttQos, username: mqttUser, payloadFormat: mqttPayloadFormat, parseRegex });
        }

        payload = {
          type: 'industrial',
          protocol,
          language,
          name: scriptName.trim() || undefined,
          industrialConfig: baseIndustrial,
          options: {
            customHeader,
            licenseDays: licenseDays || undefined,
            licenseClient: licenseClient.trim() || clientName || undefined,
          },
        };
      } else {
        if (!selectedPipeline) {
          setError(t('script_generator.error_no_pipeline') || 'Selecciona un pipeline');
          setIsGenerating(false);
          return;
        }
        payload = {
          type: 'pipeline',
          mode: useAI ? 'ai' : 'template',
          idConfigHead: parseInt(selectedPipeline),
          language,
          name: scriptName.trim() || undefined,
          options: {
            includeLogging, includeRetry, includeSchedule, customHeader,
            licenseDays: licenseDays || undefined,
            licenseClient: licenseClient.trim() || clientName || undefined,
          },
        };
      }

      const res = await apiClient.post('/dashboard/generate-script', payload);
      if (res.data.success) {
        setGeneratedScript(res.data.script);
        setGeneratedFilename(res.data.filename);
        setGeneratedTest(res.data.testContent || '');
        setGeneratedGuide(res.data.guideContent || '');
        setGeneratedTestFilename(res.data.testFilename || '');
        setPreviewTab('script');
        fetchData();
      }
    } catch (err) {
      const code = err?.response?.data?.detail?.code || '';
      const msgs = {
        ERR_SUPERADMIN_ONLY: t('script_generator.error_superadmin') || 'Solo SuperAdmin puede generar scripts',
        ERR_SCRIPT_RATE_LIMIT: t('script_generator.error_rate_limit') || 'Límite alcanzado (10/hora)',
        ERR_NO_AI_KEY: t('script_generator.error_no_ai_key') || 'API key de IA no configurada',
        ERR_PIPELINE_NOT_FOUND: t('script_generator.error_pipeline_not_found') || 'Pipeline no encontrado',
      };
      setError(msgs[code] || err.message || t('script_generator.error_generic') || 'Error al generar');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── View existing script ───────────────────────────────────────────
  const handleViewScript = async (scriptId) => {
    setLoadingView(true);
    try {
      const res = await apiClient.get(`/dashboard/scripts/${scriptId}`);
      if (res.data.success) {
        setViewingScript(res.data.data);
        setPreviewTab('script');
      }
    } catch (err) {
      console.error('Error cargando script:', err);
    } finally {
      setLoadingView(false);
    }
  };

  // ── Delete script ──────────────────────────────────────────────────
  const confirmDelete = (scriptId, name) => {
    setModalConfig({
      isOpen: true,
      title: t('script_generator.delete_title') || 'Eliminar script',
      message: `${t('script_generator.delete_msg') || '¿Eliminar'} "${name}"?`,
      showCancel: true,
      onAccept: async () => {
        try {
          await apiClient.delete(`/dashboard/scripts/${scriptId}`);
          fetchData();
          closeModal();
        } catch (err) {
          console.error('Error eliminando:', err);
          closeModal();
        }
      },
    });
  };

  // ── Toggle favorite ────────────────────────────────────────────────
  const handleToggleFavorite = async (scriptId) => {
    try {
      const res = await apiClient.patch(`/dashboard/scripts/${scriptId}/favorite`);
      if (res.data.success) {
        setScripts(prev => prev.map(s =>
          s.id === scriptId ? { ...s, isFavorite: res.data.isFavorite } : s
        ));
      }
    } catch (err) { console.error('Error toggling favorite:', err); }
  };

  // ── Duplicate script ───────────────────────────────────────────────
  const handleDuplicate = async (scriptId) => {
    try {
      const res = await apiClient.post(`/dashboard/scripts/${scriptId}/duplicate`, {});
      if (res.data.success) fetchData();
    } catch (err) { console.error('Error duplicating:', err); }
  };

  // ── Copy / Download ────────────────────────────────────────────────
  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = content;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadZip = async () => {
    // Dynamic import of JSZip — included via CDN or npm
    try {
      const content = viewingScript?.scriptContent || generatedScript;
      const testContent = viewingScript?.testContent || generatedTest;
      const guideContent = viewingScript?.guideContent || generatedGuide;
      const fname = viewingScript?.filename || generatedFilename;
      const tFname = viewingScript?.testFilename || generatedTestFilename || `test_${fname}`;

      // Simple multi-file download as individual files
      if (content) handleDownload(content, fname);
      if (testContent) {
        setTimeout(() => handleDownload(testContent, tFname), 300);
      }
      if (guideContent) {
        const baseName = fname.replace(/\.[^.]+$/, '');
        setTimeout(() => handleDownload(guideContent, `README_${baseName}.md`), 600);
      }
    } catch (err) {
      console.error('Error descargando paquete:', err);
    }
  };

  // ── Pagination ─────────────────────────────────────────────────────
  const filteredScripts = scripts.filter(s => {
    if (filterLang !== 'all' && s.language !== filterLang) return false;
    if (filterType !== 'all' && (s.generationType || 'pipeline') !== filterType) return false;
    return true;
  });
  const sortedScripts = [...filteredScripts].sort((a, b) => {
    if ((b.isFavorite || 0) !== (a.isFavorite || 0)) return (b.isFavorite || 0) - (a.isFavorite || 0);
    return (b.createdAt || 0) - (a.createdAt || 0);
  });
  const totalPages = Math.ceil(sortedScripts.length / ITEMS_PER_PAGE);
  const paginatedScripts = sortedScripts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // ── Pipeline options ───────────────────────────────────────────────
  const pipelineOptions = pipelines
    .filter(p => p.status !== 0)
    .map(p => ({
      value: p.id,
      label: `(${p.id}) ${p.name} — ${(p.sourceType || '').toUpperCase()} → ${(p.targetType || '').toUpperCase()}`,
    }));

  // ── Format date ────────────────────────────────────────────────────
  const fmtDate = (unix) => {
    if (!unix) return '—';
    return new Date(unix * 1000).toLocaleDateString(uiLang === 'en' ? 'en-US' : 'es-PE', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const langIcon = (lang) => {
    const l = LANGUAGES.find(x => x.value === lang);
    return l ? l.icon : '📄';
  };

  // ══════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      <AppSidebar user={user} onLogout={handleLogout} />

      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={openSidebar} aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0">
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow" className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow" className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
          </div>
          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{clientName}</p>
                <p className="text-xs text-slate-500">{user?.name}</p>
              </div>
              <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition" title={t('dashboard.logout')}>
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Page Content ────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Title ───────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-50 text-violet-600 rounded-xl flex items-center justify-center">
              <FileCode2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-slate-800">
                {t('script_generator.title') || 'Generador de Scripts'}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {t('script_generator.subtitle') || 'Genera código standalone para consultoría'}
              </p>
            </div>
          </div>
          <button onClick={fetchData} disabled={loadingList}
            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition">
            <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* ══════════ LEFT: Generator Form ══════════ */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

              {/* Tabs */}
              <div className="flex border-b border-slate-100">
                <button onClick={() => !isGenerating && setActiveTab('pipeline')}
                  className={`flex-1 px-4 py-3 text-xs font-bold transition border-b-2 ${activeTab === 'pipeline'
                    ? 'border-indigo-500 text-indigo-700 bg-indigo-50/50'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}>
                  <Terminal className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                  {t('script_generator.tab_pipeline') || 'Pipeline'}
                </button>
                <button onClick={() => !isGenerating && setActiveTab('industrial')}
                  className={`flex-1 px-4 py-3 text-xs font-bold transition border-b-2 ${activeTab === 'industrial'
                    ? 'border-indigo-500 text-indigo-700 bg-indigo-50/50'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}>
                  <Cpu className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                  {t('script_generator.tab_industrial') || 'Captura Industrial'}
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* Script name (common) */}
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    {t('script_generator.script_name') || 'Nombre del script'} <span className="text-red-400">*</span>
                  </label>
                  <input value={scriptName} onChange={e => setScriptName(e.target.value)}
                    placeholder={t('script_generator.name_placeholder') || 'Migración Clientes ACME'}
                    maxLength={200}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl outline-none focus:border-indigo-300 transition" />
                </div>

                {/* ── Pipeline tab ───────────────────────────────────── */}
                {activeTab === 'pipeline' && (
                  <>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Pipeline</label>
                      <select value={selectedPipeline} onChange={e => setSelectedPipeline(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl outline-none focus:border-indigo-300 transition">
                        <option value="">{t('script_generator.select_placeholder') || '— Seleccionar —'}</option>
                        {pipelineOptions.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                        {t('script_generator.language') || 'Lenguaje'}
                      </label>
                      <div className="flex gap-2">
                        {LANGUAGES.map(l => (
                          <button key={l.value} onClick={() => setLanguage(l.value)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition ${language === l.value
                              ? 'border-indigo-400 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-100'
                              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                              }`}>
                            <span>{l.icon}</span> {l.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        {t('script_generator.options') || 'Opciones'}
                      </label>
                      {[
                        { key: 'logging', state: includeLogging, set: setIncludeLogging, label: t('script_generator.opt_logging') || 'Logging con timestamps' },
                        { key: 'retry', state: includeRetry, set: setIncludeRetry, label: t('script_generator.opt_retry') || 'Retry con backoff' },
                        { key: 'schedule', state: includeSchedule, set: setIncludeSchedule, label: t('script_generator.opt_schedule') || 'CRON (comentado)' },
                      ].map(opt => (
                        <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={opt.state} onChange={e => opt.set(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                          <span className="text-xs text-slate-600">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    {/* Toggle IA */}
                    <div className="flex items-center justify-between px-3 py-2.5 bg-violet-50 border border-violet-200 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-violet-500" />
                        <div>
                          <span className="text-xs font-bold text-violet-700">
                            {t('script_generator.ai_mode') || 'Generación con IA'}
                          </span>
                          <p className="text-[10px] text-violet-400">
                            {useAI
                              ? (t('script_generator.ai_on') || 'LLM genera el código (~$0.05, 15-30s)')
                              : (t('script_generator.ai_off') || 'Template determinístico ($0, instantáneo)')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setUseAI(!useAI)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${useAI ? 'bg-violet-500' : 'bg-slate-300'
                          }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${useAI ? 'translate-x-4' : 'translate-x-0.5'
                          }`} />
                      </button>
                    </div>
                    {useAI && estimatedCost && (
                      <p className="text-[10px] text-amber-600 -mt-1 ml-1 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {t('script_generator.cost_estimate') || 'Costo estimado'}: ~${estimatedCost.estimatedCost.toFixed(2)}
                        <span className="text-slate-400">({estimatedCost.provider})</span>
                      </p>
                    )}
                  </>
                )}

                {/* ── Serial tab ─────────────────────────────────────── */}
                {activeTab === 'industrial' && (
                  <>
                    {/* Protocol selector */}
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                        {t('script_generator.protocol') || 'Protocolo'}
                      </label>
                      <div className="grid grid-cols-2 gap-1.5">
                        {PROTOCOLS.map(p => (
                          <button key={p.value} onClick={() => setProtocol(p.value)}
                            className={`px-2.5 py-2 rounded-xl text-left border transition ${protocol === p.value
                              ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-100'
                              : 'border-slate-200 hover:bg-slate-50'
                              }`}>
                            <span className="text-xs font-bold text-slate-700">{p.label}</span>
                            <p className="text-[9px] text-slate-400">{p.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Lenguaje (industrial) */}
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                        {t('script_generator.language') || 'Lenguaje'}
                      </label>
                      <div className="flex gap-2">
                        {LANGUAGES.map(l => (
                          <button key={l.value} onClick={() => setLanguage(l.value)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition ${language === l.value
                              ? 'border-indigo-400 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-100'
                              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                              }`}>
                            <span>{l.icon}</span> {l.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Serial fields — solo si protocol === 'serial' */}
                    {protocol === 'serial' && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                              {t('script_generator.serial_port') || 'Puerto'}
                            </label>
                            <input value={serialPort} onChange={e => setSerialPort(e.target.value)}
                              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Baudrate</label>
                            <select value={baudrate} onChange={e => setBaudrate(parseInt(e.target.value))}
                              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                              {BAUDRATE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Parity</label>
                            <select value={parity} onChange={e => setParity(e.target.value)}
                              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                              {PARITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Stop bits</label>
                            <select value={stopbits} onChange={e => setStopbits(parseInt(e.target.value))}
                              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                              <option value={1}>1</option><option value={2}>2</option>
                            </select>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Modbus fields */}
                    {protocol === 'modbus' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tipo</label>
                          <select value={modbusType} onChange={e => setModbusType(e.target.value)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                            <option value="TCP">TCP</option><option value="RTU">RTU (Serial)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Host</label>
                          <input value={modbusHost} onChange={e => setModbusHost(e.target.value)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Puerto</label>
                          <input type="number" value={modbusPort} onChange={e => setModbusPort(parseInt(e.target.value) || 502)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Slave ID</label>
                          <input type="number" value={slaveId} onChange={e => setSlaveId(parseInt(e.target.value) || 1)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Registro inicio</label>
                          <input type="number" value={registerAddress} onChange={e => setRegisterAddress(parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Cantidad</label>
                          <input type="number" value={registerCount} onChange={e => setRegisterCount(parseInt(e.target.value) || 10)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Polling (seg)</label>
                          <input type="number" value={pollingInterval} onChange={e => setPollingInterval(parseInt(e.target.value) || 5)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                      </div>
                    )}

                    {/* TCP Socket fields */}
                    {protocol === 'tcp_socket' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Host</label>
                          <input value={tcpHost} onChange={e => setTcpHost(e.target.value)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Puerto</label>
                          <input type="number" value={tcpPort} onChange={e => setTcpPort(parseInt(e.target.value) || 9100)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Delimitador</label>
                          <select value={tcpDelimiter} onChange={e => setTcpDelimiter(e.target.value)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                            <option value="newline">Salto de línea</option>
                            <option value="null_byte">Byte nulo</option>
                            <option value="fixed_length">Largo fijo</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Timeout (seg)</label>
                          <input type="number" value={tcpTimeout} onChange={e => setTcpTimeout(parseInt(e.target.value) || 10)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                      </div>
                    )}

                    {/* MQTT fields */}
                    {protocol === 'mqtt' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Broker</label>
                          <input value={mqttHost} onChange={e => setMqttHost(e.target.value)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Puerto</label>
                          <input type="number" value={mqttPort} onChange={e => setMqttPort(parseInt(e.target.value) || 1883)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Topic(s)</label>
                          <input value={mqttTopics} onChange={e => setMqttTopics(e.target.value)}
                            placeholder="planta/sensor1, planta/sensor2"
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">QoS</label>
                          <select value={mqttQos} onChange={e => setMqttQos(parseInt(e.target.value))}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                            <option value={0}>0 - At most once</option>
                            <option value={1}>1 - At least once</option>
                            <option value={2}>2 - Exactly once</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Formato</label>
                          <select value={mqttPayloadFormat} onChange={e => setMqttPayloadFormat(e.target.value)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                            <option value="json">JSON</option>
                            <option value="raw">Raw text</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Regex de parsing — todos los protocolos */}
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                        {t('script_generator.parse_regex') || 'Regex de parsing'}
                      </label>
                      <input value={parseRegex} onChange={e => setParseRegex(e.target.value)}
                        placeholder="(.+)"
                        className={`w-full px-3 py-2 text-xs font-mono border rounded-xl outline-none transition ${regexError ? 'border-red-300 bg-red-50/50' : 'border-slate-200'}`} />
                      {regexError && (
                        <p className="mt-1 text-[10px] text-red-500 font-mono truncate" title={regexError}>
                          ⚠ {regexError}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                        {t('script_generator.test_string') || 'String de prueba'}
                      </label>
                      <input value={testString} onChange={e => setTestString(e.target.value)}
                        className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-xl outline-none" />
                      {regexPreview.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                          {regexPreview.map((g, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] font-mono text-emerald-700">
                              ${i + 1}: {g}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Modo de operación — aplica a todos los protocolos */}
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                        {t('script_generator.serial_mode') || 'Modo de operación'}
                      </label>
                      <div className="flex gap-2">
                        {['daemon', 'oneshot', 'scheduled'].map(m => (
                          <button key={m} onClick={() => setSerialMode(m)}
                            className={`px-3 py-2 rounded-xl text-xs font-bold border transition ${serialMode === m
                              ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                              }`}>
                            {m === 'daemon' ? (t('script_generator.mode_daemon') || 'Daemon') : m === 'scheduled' ? (t('script_generator.mode_scheduled') || 'Programado (CRON)') : (t('script_generator.mode_oneshot') || 'One-shot')}
                          </button>
                        ))}
                      </div>
                      {serialMode === 'scheduled' && (
                        <div className="mt-2 flex items-center gap-2">
                          <label className="text-[10px] text-slate-500 font-medium">
                            {t('script_generator.cron_every') || 'Ejecutar cada'}
                          </label>
                          <input type="number" min={1} max={1440} value={cronInterval}
                            onChange={e => setCronInterval(Math.min(1440, Math.max(1, parseInt(e.target.value) || 5)))}
                            className="w-16 px-2 py-1 text-xs border border-slate-200 rounded-lg text-center outline-none focus:border-indigo-400" />
                          <span className="text-[10px] text-slate-400">{t('script_generator.cron_minutes') || 'minutos'}</span>
                        </div>
                      )}
                    </div>
                    {/* Destination */}
                    <div className="border-t border-slate-100 pt-3">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                        {t('script_generator.dest_db') || 'BD Destino'}
                      </label>
                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <select value={destEngine} onChange={e => setDestEngine(e.target.value)}
                          className="px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none">
                          <option value="mysql">MySQL</option>
                          <option value="postgres">PostgreSQL</option>
                          <option value="mssql">SQL Server</option>
                        </select>
                        <input value={destTable} onChange={e => setDestTable(e.target.value)} placeholder="tabla"
                          className="px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none" />
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-slate-400 uppercase">{t('script_generator.columns') || 'Columnas'}</span>
                          <button onClick={addColumn}
                            disabled={destColumns.some(c => !c.name.trim())}
                            className={`transition ${destColumns.some(c => !c.name.trim()) ? 'text-slate-300 cursor-not-allowed' : 'text-indigo-500 hover:text-indigo-700'}`}>
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {destColumns.map((col, idx) => (
                          <div key={idx} className="flex items-center gap-1.5">
                            <input value={col.name} onChange={e => updateColumn(idx, 'name', e.target.value)} placeholder="nombre"
                              className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none" />
                            {editingTypeIdx === idx ? (
                              <input
                                autoFocus
                                defaultValue={col.type}
                                onBlur={e => {
                                  const v = e.target.value.trim().toUpperCase();
                                  if (isValidDbType(v)) {
                                    updateColumn(idx, 'type', v);
                                  }
                                  setEditingTypeIdx(null);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') e.target.blur();
                                  if (e.key === 'Escape') setEditingTypeIdx(null);
                                }}
                                placeholder="BIGINT UNSIGNED"
                                className="w-40 px-2 py-1.5 text-xs font-mono border border-indigo-300 rounded-lg outline-none ring-2 ring-indigo-100"
                              />
                            ) : (
                              <select
                                value={DB_COLUMN_TYPES.includes(col.type) ? col.type : '__show_custom'}
                                onChange={e => {
                                  const val = e.target.value;
                                  if (val === '__edit' || val === '__show_custom') {
                                    setEditingTypeIdx(idx);
                                  } else {
                                    updateColumn(idx, 'type', val);
                                  }
                                }}
                                className="w-40 px-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none">
                                {DB_COLUMN_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                                {col.type && !DB_COLUMN_TYPES.includes(col.type) && (
                                  <option value="__show_custom">{col.type}</option>
                                )}
                                <option value="__edit">✏️ Otro...</option>
                              </select>
                            )}
                            <button onClick={() => removeColumn(idx)} className="text-slate-300 hover:text-red-500 p-0.5">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Licencia */}
                <div className="border-t border-slate-100 pt-3">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    {t('script_generator.license') || 'Licencia'}
                  </label>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">
                        {t('script_generator.license_client') || 'Cliente licenciado'} <span className="text-red-400">*</span>
                      </label>
                      <input value={licenseClient} onChange={e => setLicenseClient(e.target.value)}
                        placeholder={clientName || 'Nombre del cliente'}
                        maxLength={200}
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:border-indigo-300 transition" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">
                        {t('script_generator.license_duration') || 'Vigencia'}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {LICENSE_PRESETS.map(p => (
                          <button key={p.value} onClick={() => setLicenseDays(p.value)}
                            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition ${licenseDays === p.value
                              ? 'border-indigo-400 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100'
                              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                              }`}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom header */}
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    {t('script_generator.custom_header') || 'Encabezado personalizado'} <span className="text-red-400">*</span>
                  </label>
                  <textarea value={customHeader} onChange={e => setCustomHeader(e.target.value)}
                    placeholder="# Script para Cliente X" rows={2}
                    className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-xl outline-none resize-none" />
                </div>

                {error && (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl">
                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}

                {/* Generate button */}
                <button onClick={handleGenerate}
                  disabled={isGenerating || !user?.canGenerateScript || !scriptName.trim() || !customHeader.trim() || !(licenseClient.trim() || clientName) || (activeTab === 'pipeline' && !selectedPipeline) || (activeTab === 'industrial' && (destColumns.some(c => !c.name.trim()) || !!regexError || !parseRegex.trim()))}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-white bg-violet-600 hover:bg-violet-700 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
                  {isGenerating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t('script_generator.generating') || 'Generando...'}</>
                  ) : (
                    <><Play className="w-4 h-4" fill="currentColor" /> {t('script_generator.generate') || 'Generar Script'}</>
                  )}
                </button>
                {!user?.canGenerateScript && (
                  <p className="text-[10px] text-amber-600 text-center mt-1">
                    {t('script_generator.generation_disabled') || 'Generación no habilitada para tu usuario. Contacta al administrador.'}
                  </p>
                )}

                <p className="text-[10px] text-slate-400 italic text-center">
                  {t('script_generator.disclaimer') || 'Revisar antes de entregar al cliente.'}
                </p>
              </div>
            </div>
          </div>

          {/* ══════════ RIGHT: Results / History ══════════ */}
          <div className="lg:col-span-3 space-y-4">

            {/* ── Generated script preview ─────────────────────────── */}
            {(generatedScript || viewingScript) && (() => {
              const scriptContent = viewingScript?.scriptContent || generatedScript;
              const testContent = viewingScript?.testContent || generatedTest;
              const guideContent = viewingScript?.guideContent || generatedGuide;
              const fname = viewingScript?.filename || generatedFilename;
              const activeContent = previewTab === 'test' ? testContent : previewTab === 'guide' ? guideContent : scriptContent;
              const activeLabel = previewTab === 'test'
                ? (viewingScript?.testFilename || generatedTestFilename || `test_${fname}`)
                : previewTab === 'guide' ? `README_${fname.replace(/\.[^.]+$/, '')}.md` : fname;

              return (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                    <div className="flex items-center gap-2">
                      <FileCode2 className="w-4 h-4 text-violet-500" />
                      <span className="text-xs font-bold text-slate-700">{activeLabel}</span>
                      <span className="text-[10px] text-slate-400">
                        ({((activeContent || '').length / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => handleCopy(activeContent || '')}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition">
                        {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                        {copied ? (t('script_generator.copied') || 'Copiado') : (t('script_generator.copy') || 'Copiar')}
                      </button>
                      <button onClick={() => handleDownload(activeContent || '', activeLabel)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition">
                        <Download className="w-3 h-3" /> {t('script_generator.download') || 'Descargar'}
                      </button>
                      <button onClick={handleDownloadZip}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition"
                        title={t('script_generator.download_package') || 'Descargar paquete'}>
                        <Package className="w-3 h-3" /> {t('script_generator.package') || 'Paquete'}
                      </button>
                      <button onClick={() => { setGeneratedScript(''); setViewingScript(null); setGeneratedTest(''); setGeneratedGuide(''); }}
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Tabs: Script | Test | Guía */}
                  <div className="flex border-b border-slate-100">
                    {[
                      { key: 'script', icon: FileCode2, label: t('script_generator.tab_script') || 'Script' },
                      { key: 'test', icon: TestTube2, label: t('script_generator.tab_test') || 'Test', disabled: !testContent },
                      { key: 'guide', icon: BookOpen, label: t('script_generator.tab_guide') || 'Guía', disabled: !guideContent },
                    ].map(tab => (
                      <button key={tab.key}
                        onClick={() => !tab.disabled && setPreviewTab(tab.key)}
                        disabled={tab.disabled}
                        className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold border-b-2 transition ${previewTab === tab.key
                          ? 'border-violet-500 text-violet-700 bg-violet-50/50'
                          : tab.disabled
                            ? 'border-transparent text-slate-300 cursor-not-allowed'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                          }`}>
                        <tab.icon className="w-3 h-3" />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <pre className="bg-slate-900 text-slate-100 text-[11px] leading-relaxed p-4 overflow-auto max-h-[60vh] font-mono whitespace-pre-wrap break-words">
                    {activeContent || `(${previewTab === 'test' ? 'Sin test generado' : 'Sin guía generada'})`}
                  </pre>
                </div>
              );
            })()}

            {/* ── Script History ────────────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-xs font-black text-slate-600 uppercase tracking-wider">
                    {t('script_generator.history') || 'Scripts generados'}
                    <span className="ml-2 text-slate-400 font-normal normal-case">
                      ({filteredScripts.length}{filteredScripts.length !== scripts.length ? `/${scripts.length}` : ''})
                    </span>
                  </h2>
                  {scripts.length > 0 && (
                    <div className="flex items-center gap-2">
                      <select value={filterLang} onChange={e => setFilterLang(e.target.value)}
                        className="text-[10px] bg-white border border-slate-200 rounded-lg px-2 py-1 text-slate-600 font-medium">
                        <option value="all">{t('script_generator.filter_all_langs') || 'Todos'}</option>
                        <option value="python">Python</option>
                        <option value="nodejs">Node.js</option>
                        <option value="csharp">C#</option>
                      </select>
                      <select value={filterType} onChange={e => setFilterType(e.target.value)}
                        className="text-[10px] bg-white border border-slate-200 rounded-lg px-2 py-1 text-slate-600 font-medium">
                        <option value="all">{t('script_generator.filter_all_types') || 'Todos'}</option>
                        <option value="pipeline">{t('script_generator.tab_pipeline') || 'Pipeline'}</option>
                        <option value="industrial">{t('script_generator.tab_industrial') || 'Industrial'}</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {loadingList ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
              ) : filteredScripts.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileCode2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm font-medium">{t('script_generator.empty') || 'Sin scripts generados'}</p>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-slate-100">
                    {paginatedScripts.map(s => (
                      <div key={s.id} className="px-4 py-3 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm">{langIcon(s.language)}</span>
                              <p className="text-sm font-bold text-slate-700 truncate">{s.name}</p>
                              <div className="flex items-center gap-1">
                                <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${s.language === 'python' ? 'bg-yellow-50 text-yellow-700' :
                                  s.language === 'nodejs' ? 'bg-green-50 text-green-700' :
                                    'bg-blue-50 text-blue-700'
                                  }`}>
                                  {s.language === 'python' ? 'Python' : s.language === 'nodejs' ? 'Node.js' : 'C#'}
                                </span>
                                {s.generationMode === 'ai' && (
                                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded">IA</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] text-slate-400">
                              <span className="font-mono">{s.filename}</span>
                              <span>·</span>
                              <span className="flex items-center gap-0.5">
                                <Clock className="w-2.5 h-2.5" /> {fmtDate(s.createdAt)}
                              </span>
                              {s.pipelineName && (
                                <>
                                  <span>·</span>
                                  <span className="text-indigo-500 font-medium">{s.pipelineName}</span>
                                </>
                              )}
                              <span>·</span>
                              <span>{(s.scriptSize / 1024).toFixed(1)} KB</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => handleToggleFavorite(s.id)}
                              className={`p-1.5 rounded-lg transition ${s.isFavorite ? 'text-amber-500 bg-amber-50' : 'text-slate-300 hover:text-amber-400 hover:bg-amber-50'}`}
                              title={t('script_generator.favorite') || 'Favorito'}>
                              <Star className="w-4 h-4" fill={s.isFavorite ? 'currentColor' : 'none'} />
                            </button>
                            <button onClick={() => handleViewScript(s.id)}
                              disabled={loadingView}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                              title={t('script_generator.view') || 'Ver'}>
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDuplicate(s.id)}
                              className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition"
                              title={t('script_generator.duplicate') || 'Duplicar'}>
                              <CopyPlus className="w-4 h-4" />
                            </button>
                            {s.hasTest && (
                              <span className="px-1 py-0.5 text-[8px] font-bold bg-emerald-50 text-emerald-600 rounded"
                                title={t('script_generator.has_test') || 'Incluye test'}>
                                <TestTube2 className="w-3 h-3 inline" />
                              </span>
                            )}
                            <button onClick={() => confirmDelete(s.id, s.name)}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                              title={t('script_generator.delete_btn') || 'Eliminar'}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                      <p className="text-xs text-slate-400">{filteredScripts.length} scripts</p>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                          className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition">
                          <ChevronLeft className="w-4 h-4 text-slate-500" />
                        </button>
                        <span className="text-xs text-slate-500 px-2">{currentPage}/{totalPages}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                          className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition">
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        onClose={closeModal}
        onAccept={modalConfig.onAccept}
        showCancel={modalConfig.showCancel}
      />
    </div>
  );
};
